import { useCallback, useEffect, useState } from 'react';
import './App.css';
import Aside from './Common/Aside';
import Header from './Common/Header';
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import Dashboard from './Pages/Dashboard';
import Users from './Pages/Users';
import LogsHistory from './Pages/LogsHistory';
import Accounts from './Pages/Accounts';
import { useDispatch, useSelector } from 'react-redux';
import Login from './Pages/Login';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { usersActions } from './store/user';
import ActionsHistory from './Pages/ActionsHistory';
import WonCars from './Pages/WonCars';
import Alerts from './Pages/Alerts';
import Notifications from './Pages/Notifications';
import WatchList from './Pages/WatchList';
import PrivacyPolicy from './Pages/PrivacyPolicy';
import ExtensionPrivacyPolicy from './Pages/ExtensionPrivacyPolicy';
import api from './lib/api';

function App() {
  const [showFullWidth, setShowFullWidth] = useState(true);
  const user = useSelector((state) => state.user);
  const [notification, setNotification] = useState({ type: "", text: "", show: false });
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    const controller = new AbortController();

    api.get('/is-logged-in', { signal: controller.signal })
      .then((res) => {
        dispatch(usersActions.saveUserData(res.data.data));
        dispatch(usersActions.changeIsLoggedIn(true));
      })
      .catch((err) => {
        // An aborted request is not a failed login check; without this guard
        // React 18's double-mounted dev effect logged the user straight out.
        if (err.code === "ERR_CANCELED" || err.name === "CanceledError") return;
        dispatch(usersActions.changeIsLoggedIn(false));
      });

    return () => controller.abort();
  }, [dispatch]);

  const closeNotification = useCallback(
    () => setNotification((prev) => ({ ...prev, show: false })),
    []
  );

  // Public pages: store listings require a privacy policy URL that anyone can
  // open, so these must render before (and regardless of) the login gate below.
  if (location.pathname === "/privacy-policy") {
    return <PrivacyPolicy />;
  }
  if (location.pathname === "/extension-privacy-policy") {
    return <ExtensionPrivacyPolicy />;
  }

  return (
    <div className="root">
      {/* A Snackbar instead of an absolutely-positioned Alert: the old toast
          was placed with position:absolute inside .root, so it scrolled away
          with the page and sat underneath modals. */}
      <Snackbar
        open={notification.show}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={closeNotification}
        sx={{ zIndex: 2000 }}
      >
        <Alert
          onClose={closeNotification}
          severity={notification.type || "info"}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {notification.text}
        </Alert>
      </Snackbar>

      {/* Null means "still checking"; rendering the login form during that
          window made it flash on every page load for a signed-in user. */}
      {user.isLoggedIn === null && <div className="app-boot-loader"><div className="dt-loader"></div></div>}

      {user.isLoggedIn === true && <>
        <div className={`aside-container ${showFullWidth ? '' : "minimized-aside-container"}`}>
          <Aside showFullWidth={showFullWidth} />
        </div>
        <div className={`website-main-content ${showFullWidth ? "" : "website-main-content-minimized-aside"}`}>
          <Header setNotification={setNotification} setShowFullWidth={setShowFullWidth} />
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/users" element={<Users setNotification={setNotification} />} />
            <Route path="/accounts" element={<Accounts setNotification={setNotification} />} />
            <Route path="/bid-history" element={<LogsHistory setNotification={setNotification} />} />
            <Route path="/actions-history" element={<ActionsHistory setNotification={setNotification} />} />
            <Route path="/won-vehicles" element={<WonCars setNotification={setNotification} />} />
            <Route path="/watchlist" element={<WatchList setNotification={setNotification} />} />
            {user.role === "admin" && <Route path="/alerts" element={<Alerts setNotification={setNotification} />} />}
            {user.role === "admin" && <Route path="/notifications" element={<Notifications setNotification={setNotification} />} />}
            {/* Catch-all: an unknown or admin-only URL used to render an empty
                main area with no explanation. */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </>}

      {user.isLoggedIn === false && <Login setNotification={setNotification} />}
    </div>
  );
}

export default App;
